import { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GripVertical, X, UserPlus } from 'lucide-react';
import { DateTime } from 'luxon';
import type { SignerEntry } from '../../utils';
import type { Annotation } from '../../../components/annotation/annotation';
import { toast } from '@/components/toast/toast';
import styles from './add-signers-panel.module.css';

type AddSignersPanelProps = {
	signers: SignerEntry[];
	selectedSignerValue: string | null;
	annotations: Annotation[];
	onAddSigner: (signer: Omit<SignerEntry, 'color'>) => void;
	onRemoveSigner: (signerValue: string) => void;
	onSelectSigner: (signerValue: string) => void;
	onReorderSigners: (fromIndex: number, toIndex: number) => void;
	/** Whether the first signer chip (the document owner) is locked in place. Defaults to true. */
	fixedFirstSigner?: boolean;
};

const SIGNER_CHIP_TYPE = 'SIGNER_CHIP';

type DraggedSignerChip = { index: number };

type SignerChipProps = {
	signer: SignerEntry;
	index: number;
	isFixed: boolean;
	isSelected: boolean;
	count: number;
	onSelectSigner: (signerValue: string) => void;
	onRemoveSigner: (signerValue: string) => void;
	onReorderSigners: (fromIndex: number, toIndex: number) => void;
};

const SignerChip = ({
	signer,
	index,
	isFixed,
	isSelected,
	count,
	onSelectSigner,
	onRemoveSigner,
	onReorderSigners,
}: SignerChipProps) => {
	const ref = useRef<HTMLDivElement>(null);

	const [{ isDragging }, drag] = useDrag<
		DraggedSignerChip,
		unknown,
		{ isDragging: boolean }
	>({
		type: SIGNER_CHIP_TYPE,
		item: { index },
		canDrag: !isFixed,
		collect: (monitor) => ({ isDragging: monitor.isDragging() }),
	});

	const [, drop] = useDrop<DraggedSignerChip>({
		accept: SIGNER_CHIP_TYPE,
		canDrop: () => !isFixed,
		hover: (draggedItem) => {
			if (isFixed || draggedItem.index === index) return;
			onReorderSigners(draggedItem.index, index);
			draggedItem.index = index;
		},
	});

	drag(drop(ref));

	return (
		<div
			ref={ref}
			className={[
				styles.chip,
				isSelected ? styles['chip--active'] : '',
				isDragging ? styles['chip--dragging'] : '',
			].join(' ')}
			style={{
				borderColor: signer.color,
				backgroundColor: isSelected
					? signer.color + '44'
					: signer.color + '18',
			}}
			onClick={() => onSelectSigner(signer.value)}
			title={`${signer.value} · ${count} anotación${count !== 1 ? 'es' : ''}`}
		>
			{!isFixed && (
				<GripVertical size={12} className={styles['chip-handle']} />
			)}
			<span
				className={styles['chip-avatar']}
				style={{ backgroundColor: signer.color }}
			>
				{signer.label.charAt(0)}
			</span>
			<span className={styles['chip-badge']}>#{signer.order}</span>
			<span className={styles['chip-label']}>{signer.label}</span>
			{!isFixed && (
				<button
					type="button"
					className={styles['chip-remove']}
					onClick={(e) => {
						e.stopPropagation();
						onRemoveSigner(signer.value);
					}}
				>
					<X size={10} />
				</button>
			)}
		</div>
	);
};

const defaultExpiration = () =>
	DateTime.now().plus({ months: 1 }).toISODate() ?? '';

export const AddSignersPanel = ({
	signers,
	selectedSignerValue,
	annotations,
	onAddSigner,
	onRemoveSigner,
	onSelectSigner,
	onReorderSigners,
	fixedFirstSigner = true,
}: AddSignersPanelProps) => {
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [email, setEmail] = useState('');
	const [expiration, setExpiration] = useState(defaultExpiration);

	const selectedSigner = signers.find((s) => s.value === selectedSignerValue);

	const handleAdd = () => {
		const trimmed = email.trim().toLowerCase();
		if (!trimmed) return;
		if (signers.some((s) => s.value.toLowerCase() === trimmed)) {
			toast({
				mode: 'caution',
				description: 'Este firmante ya fue agregado',
			});
			return;
		}
		onAddSigner({
			value: trimmed,
			label: trimmed,
			order: signers.length + 1,
			expiration,
			role: 'signer',
		});
		setEmail('');
		setExpiration(defaultExpiration());
		setIsFormOpen(false);
	};

	return (
		<div className={styles.panel}>
			<div className={styles.header}>
				<span className={styles.title}>Firmantes</span>
				<Button
					type="button"
					size="sm"
					variant="outline"
					className="h-8 gap-1.5 px-3 text-xs"
					onClick={() => setIsFormOpen((o) => !o)}
				>
					<UserPlus className="h-3.5 w-3.5" />
					Agregar
				</Button>
			</div>

			<p className={styles.instructions}>
				Haz clic en un firmante para colocar sus anotaciones. El orden
				de la lista es el orden de firma: arrastra un firmante (
				<GripVertical size={10} style={{ display: 'inline' }} />)
				para cambiarlo.
			</p>

			<div className={styles.chips}>
				{signers.map((signer, index) => {
					const count = annotations.filter(
						(a) => a.email === signer.value,
					).length;
					return (
						<SignerChip
							key={signer.value}
							signer={signer}
							index={index}
							isFixed={fixedFirstSigner && index === 0}
							isSelected={selectedSignerValue === signer.value}
							count={count}
							onSelectSigner={onSelectSigner}
							onRemoveSigner={onRemoveSigner}
							onReorderSigners={onReorderSigners}
						/>
					);
				})}
			</div>

			{selectedSigner && (
				<p className={styles.indicator}>
					Anotando para{' '}
					<strong>{selectedSigner.label}</strong>
				</p>
			)}

			{isFormOpen && (
				<div className={styles.form}>
					<div className={styles['form-field']}>
						<Label htmlFor="signer-email">Email</Label>
						<Input
							id="signer-email"
							placeholder="correo@ejemplo.com"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onKeyDown={(e) =>
								e.key === 'Enter' && handleAdd()
							}
						/>
					</div>
					<div className={styles['form-field']}>
						<Label htmlFor="signer-expiration">Expiración</Label>
						<Input
							id="signer-expiration"
							type="date"
							value={expiration}
							onChange={(e) => setExpiration(e.target.value)}
						/>
						<span className={styles['field-hint']}>
							Fecha límite para que este firmante complete su
							firma.
						</span>
					</div>
					<div className={styles['form-actions']}>
						<Button
							type="button"
							size="sm"
							onClick={handleAdd}
							disabled={!email.trim()}
						>
							Agregar
						</Button>
						<Button
							type="button"
							size="sm"
							variant="ghost"
							onClick={() => {
								setEmail('');
								setIsFormOpen(false);
							}}
						>
							Cancelar
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};
